import React, { useState, useEffect, useCallback, KeyboardEvent, useRef, useMemo } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { upsertJournalEntry } from '../../api';
import { useGeneralContext } from '../../context';
import simpleLogo from '../../resources/logo-simple.png';
import { TagList } from '../../components/entryTags';
import { browserTimestampJournalEntryFormat, getDayDateTimeFromEntryDateTitle } from '../../util/dateTime';
import useDebounce from '../../hooks/useDebounce';
import { Colors, DEFAULT_ASSISTANT } from './types';
import { Home, X, Twitter, ArrowLeft } from 'react-feather';
import toast from 'react-hot-toast';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { EntryBlock, EntryState, EntryStates, isEntryStates } from '../../components/calendar/types';
import { AxiosError } from 'axios';
import MeatballMenu from './MeatballMenu';
import AutoResizeTextBox from './AutoResizeTextBox';
import { cleanEntryState, getWordCount } from './entryStateUtils';
import Badge from 'react-bootstrap/Badge';
import MentorSelector from './MentorSelector';
import PremiumUpsellModal from '../../components/PremiumUpsellModal';

const ONE_SECOND = 1000;
const AUTOSAVE_DELAY = ONE_SECOND * 60;

const SingleEntry = () => {
  const location = useLocation<{
    entryState: EntryStates;
  }>();

  const [entries, setEntries] = useState<EntryStates>(
    isEntryStates(location?.state?.entryState)
      ? (location?.state?.entryState as EntryStates).map((e) => ({
          ...e,
          blocks: cleanEntryState(e.blocks)
        }))
      : []
  );

  const params = useParams<{ persona: string }>();

  const generalContext = useGeneralContext();
  const { entryBackgroundColor, setEntryBackgroundColor, nuggets } = generalContext;
  const entryStates = entries.map((entry) => entry.blocks);

  const [wordCount, setWordCount] = useState<number>(
    entryStates.length > 0
      ? entryStates.reduce((prev, curr) => {
          return prev + getWordCount(curr);
        }, 0)
      : 0
  );

  const autoGeneratedTags = useMemo(() => {
    return nuggets
      .filter((nug) => entries.map((e) => e.name).includes(nug.entryName))
      .map((n) => n.tags)
      .flat();
  }, [entries, nuggets]);

  const debouncedEntryState = useDebounce<string>(JSON.stringify(entries), AUTOSAVE_DELAY);

  const initialRender = useRef(true);

  const history = useHistory();
  const [entrySaving, setEntrySaving] = useState(false);
  const [isSaved, setIsSaved] = useState(true);
  const [journalAssistant, setJournalAssistant] = useState(!!params?.persona);
  const [assistantThinking, setAssistantThinking] = useState<null | number>(null);
  const [mentor, setMentor] = useState<string>(
    params?.persona?.replace(/_/g, ' ') || localStorage.getItem('lastMentor') || DEFAULT_ASSISTANT
  );
  const [textAreaFocusIndex, setTextAreaFocusIndex] = useState<number | null>(null);
  const [blur, setBlur] = useState(false);
  const [selectedText, setSelectedText] = useState('');
  const [suggestedMentors, setSuggestedMentors] = useState<string[]>(
    localStorage.getItem('savedMentors')
      ? JSON.parse(localStorage.getItem('savedMentors') as string)
      : [DEFAULT_ASSISTANT]
  );
  const [showPremiumPaywall, setShowPremiumPaywall] = useState(false);

  useEffect(() => {
    setWordCount(
      entryStates.length > 0
        ? entryStates.reduce((prev, curr) => {
            return prev + getWordCount(curr);
          }, 0)
        : 0
    );
  }, [entryStates]);

  useEffect(() => {
    window.scrollTo(0, 0);
    document?.querySelector('body')?.scrollTo(0, 0);
  }, []);

  const updateJournalEntry = useCallback(
    async (params: { blocks: EntryBlock[]; imported: boolean; creationDateTitle: string; embedEntry: boolean }) => {
      const { blocks, imported, creationDateTitle, embedEntry } = params;

      setEntrySaving(true);
      try {
        await upsertJournalEntry({
          value: JSON.stringify({ blocks, imported }),
          creationDateTitle,
          embedEntry
        });
      } catch (error: any) {
        console.error(error);
        if (error instanceof AxiosError && error?.response?.status === 401) {
          window.alert('Unable to save because you have been logged out.');
        }
      }
      setIsSaved(true);
      setEntrySaving(false);
    },
    []
  );

  const saveEntriesAsync = useCallback(
    async (entries: EntryStates, embedEntry: boolean) => {
      for (const entry of entries) {
        await updateJournalEntry({
          embedEntry,
          blocks: entry.blocks,
          creationDateTitle: entry.name,
          imported: entry.imported
        });
      }
    },
    [updateJournalEntry]
  );

  // catch all navigation away
  useEffect(() => {
    const unblock = history.block(({ pathname }: { pathname: any }) => {
      toast
        .promise(saveEntriesAsync(entries, true), {
          loading: 'Saving',
          success: 'Entry Saved',
          error: 'Error saving entry'
        })
        .then(() => {
          // Unblock the navigation.
          unblock();
          // retry the pagination
          history.push(pathname);
        });
    });
    return unblock;
  }, [history, entries, saveEntriesAsync]);

  // save entry when text changes
  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
      return;
    }
    const debouncedEntries = JSON.parse(debouncedEntryState);
    saveEntriesAsync(debouncedEntries, false);
  }, [debouncedEntryState, saveEntriesAsync]);

  // cause browser to alert on page reload, navigation to new website, or closing of tab
  useEffect(() => {
    window.onbeforeunload = (event) => {
      const e = event || window.event;
      // Cancel the event
      e.preventDefault();
      if (e) {
        e.returnValue = ''; // Legacy method for cross browser support
      }
      return ''; // Legacy method for cross browser support
    };
    return () => {
      window.onbeforeunload = null;
    };
  }, []);

  if (entries.length === 0) {
    setEntries([
      {
        blocks: [
          {
            role: 'user',
            content: ''
          }
        ],
        imported: false,
        name: browserTimestampJournalEntryFormat(new Date()),
        tags: []
      }
    ]);
  }

  // handle cmd+s
  const handleKeyDown = (event: KeyboardEvent<HTMLImageElement>) => {
    let charCode = String.fromCharCode(event.which).toLowerCase();
    if ((event.ctrlKey || event.metaKey) && charCode === 's') {
      event.preventDefault();
      if (entrySaving) {
        return;
      }
      if (isSaved) {
        return toast.success('Entry Saved');
      }
      setEntrySaving(true);
      toast
        .promise(saveEntriesAsync(entries, false), {
          loading: 'Saving',
          success: 'Entry Saved',
          error: 'Error saving entry'
        })
        .then(() => {
          setEntrySaving(false);
          setIsSaved(true);
        });
    }
  };

  return (
    <div
      onKeyDown={handleKeyDown}
      style={{ backgroundColor: entryBackgroundColor, transition: 'background-color 600ms' }}
      className="tw-col-span-full tw-flex tw-justify-center tw-min-h-screen"
    >
      <PremiumUpsellModal
        showModal={showPremiumPaywall}
        setShowModal={setShowPremiumPaywall}
        message={`Start a FREE Premium trial to continue the conversation with ${mentor}!`}
      />
      {/* Top bar for small screens - behind icons, in front of text */}
      <div
        className="lg:tw-hidden tw-fixed tw-top-0 tw-left-0 tw-right-0 tw-h-20 tw-z-0"
        style={{ backgroundColor: entryBackgroundColor, transition: 'background-color 600ms' }}
      />
      <div className="tw-flex tw-flex-col tw-w-full tw-max-w-4xl tw-px-4 sm:tw-px-6 lg:tw-px-8">
        <div className="tw-fixed tw-top-0 tw-left-0 tw-right-0 tw-z-20 tw-pointer-events-none">
          <div className="tw-mx-auto tw-w-full tw-max-w-4xl tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-pt-6 tw-flex tw-items-center tw-justify-between">
        <OverlayTrigger placement="right" overlay={backButtonTooltip}>
          <button
            onClick={async () => {
              history.push(location?.state?.returnPath || '/home');
            }}
            style={{ backgroundColor: entryBackgroundColor, transition: 'background-color 600ms' }}
            className={`${
              blur ? 'tw-blur-sm' : ''
            } tw-pointer-events-auto tw--ml-3 tw-w-12 tw-h-12 tw-rounded-full tw-border-none tw-flex tw-items-center tw-justify-center`}
          >
            {location?.state?.returnPath ? (
              <ArrowLeft
                className={entryBackgroundColor === Colors.Dark ? 'tw-text-primary-100' : 'tw-text-primary-900'}
              />
            ) : (
              <Home className={entryBackgroundColor === Colors.Dark ? 'tw-text-primary-100' : 'tw-text-primary-900'} />
            )}
          </button>
        </OverlayTrigger>
        <MeatballMenu
          journalAssistant={journalAssistant}
          setJournalAssistant={setJournalAssistant}
          setEntryBackgroundColor={setEntryBackgroundColor}
          entryBackgroundColor={entryBackgroundColor}
          setBlur={setBlur}
          onUpgradeClick={() => {
            history.push({
              pathname: '/settings',
              state: { destination: 'choose-plan' }
            });
          }}
        />
          </div>
        </div>
        {/* Was mb-[45vh]: a viewport-relative margin whose size changed with the
            window. A fixed block of trailing space does the same job of letting
            the last line sit away from the bottom edge. */}
        <div className="tw-flex tw-flex-col tw-gap-2 tw-mb-96">
          {entries &&
            entries.map((entry, index) => {
              if (!entry.blocks) {
                return null;
              }
              return [
                ...entry.blocks.map((block, i) => {
                  const { day, date, time } = getDayDateTimeFromEntryDateTitle(entry.name);
                  return (
                    <div key={`${block.role}-${i}`}>
                      {i === 0 && (
                        <SingleEntryTitleHeader
                          key={`imported-header-${i}`}
                          entryBackgroundColor={entryBackgroundColor}
                          blur={blur}
                          entryTagStrings={entry.tags}
                          autoGeneratedTags={autoGeneratedTags}
                          setTags={(tags: string[]) => {
                            const newEntry: EntryState = {
                              ...entries[index],
                              tags
                            };
                            const newEntries: EntryStates = [
                              ...entries.slice(0, index),
                              newEntry,
                              ...entries.slice(index + 1, entries.length)
                            ];
                            setEntries(newEntries);
                          }}
                          entryDateTitle={entry.name}
                          day={day}
                          date={date}
                          time={time}
                          imported={entry.imported}
                          selectedText={selectedText}
                          journalAssistant={journalAssistant}
                          mentor={mentor}
                          setMentor={setMentor}
                          suggestedMentors={suggestedMentors}
                          setSuggestedMentors={setSuggestedMentors}
                          entryIndex={index}
                        />
                      )}
                      {block.role === 'assistant' ? (
                        <AssistantEntryBlock
                          blur={blur}
                          entryBlock={block}
                          entryBackgroundColor={entryBackgroundColor}
                          i={i}
                          entry={entry}
                          index={index}
                          setEntries={setEntries}
                        />
                      ) : (
                        <AutoResizeTextBox
                          index={i}
                          key={`textarea-${i}`}
                          blocks={entry.blocks}
                          entryBackgroundColor={entryBackgroundColor}
                          setEntryState={(entryState: EntryBlock[]) => {
                            setEntries((currentEntryState) => [
                              ...currentEntryState.slice(0, index),
                              {
                                ...currentEntryState[index],
                                blocks: entryState
                              },
                              ...currentEntryState.slice(index + 1, currentEntryState.length)
                            ]);
                          }}
                          setIsSaved={setIsSaved}
                          journalAssistant={journalAssistant}
                          setAssistantThinking={setAssistantThinking}
                          assistantThinking={assistantThinking}
                          textAreaFocusIndex={textAreaFocusIndex}
                          setTextAreaFocusIndex={setTextAreaFocusIndex}
                          blur={blur}
                          setSelectedText={setSelectedText}
                          mentor={mentor}
                          entryIndex={index}
                          setShowPremiumPaywall={setShowPremiumPaywall}
                        />
                      )}
                    </div>
                  );
                }),
                ...(assistantThinking === index
                  ? [
                      <img
                        style={{ opacity: '0.6', height: '24px', width: '24px' }}
                        alt="logo"
                        className="logo-img blinking-element"
                        src={simpleLogo}
                      ></img>
                    ]
                  : [])
              ];
            })}
        </div>
        <div className="tw-fixed tw-bottom-0 tw-left-0 tw-right-0 tw-pointer-events-none">
          <div className="tw-mx-auto tw-w-full tw-max-w-4xl tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-pb-6 tw-flex tw-justify-end">
            <OverlayTrigger placement="top" overlay={wordCountTooltip}>
              <div
                className={`${blur ? 'tw-blur-sm' : ''} ${
                  entryBackgroundColor === Colors.Dark ? 'tw-text-primary-100' : 'tw-text-primary-900'
                } tw-pointer-events-auto tw-text-xs tw-tabular-nums`}
              >
                {`${wordCount} ${wordCount === 1 ? 'word' : 'words'}`}
              </div>
            </OverlayTrigger>
          </div>
        </div>
      </div>
    </div>
  );
};

const AssistantEntryBlock = (props: {
  blur: boolean;
  entryBlock: EntryBlock;
  entryBackgroundColor: string;
  i: number;
  entry: EntryState & { tags: string[] };
  index: number;
  setEntries: React.Dispatch<React.SetStateAction<EntryStates>>;
}) => {
  const { blur, entryBlock, entryBackgroundColor, i, entry, index, setEntries } = props;
  return (
    <div
      key={`${entryBlock.role}-${i}`}
      className={`${blur ? 'tw-blur-sm' : ''} tw-relative tw-p-4 tw-pr-10 tw-rounded-lg tw-font-normal tw-text-base ${
        entryBackgroundColor === Colors.Dark
          ? 'tw-text-neutral-50 tw-bg-primary-700'
          : entryBackgroundColor === Colors.White
          ? 'tw-text-primary-900 tw-bg-primary-50'
          : 'tw-text-primary-900 tw-bg-neutral-50'
      } tw-transition-colors tw-duration-600`}
    >
      <button
        className="tw-absolute tw-top-4 tw-right-4 tw-border-none tw-bg-transparent tw-text-primary-300 tw-flex tw-justify-center tw-items-center tw-text-xs"
        onClick={() => {
          if (window.confirm('Delete this Jumble-Bot prompt?')) {
            const newEntryState = cleanEntryState([
              ...entry.blocks.slice(0, i),
              ...entry.blocks.slice(i + 1, entry.blocks.length)
            ]);
            setEntries((currentEntryState: EntryStates) => [
              ...currentEntryState.slice(0, index),
              {
                ...currentEntryState[index],
                blocks: newEntryState
              },
              ...currentEntryState.slice(index + 1, currentEntryState.length)
            ]);
          }
        }}
      >
        <X size={18} />
      </button>
      {entryBlock.content}
    </div>
  );
};

const SingleEntryTitleHeader = (params: {
  entryBackgroundColor: string;
  blur: boolean;
  day: string;
  date: string;
  time: string;
  entryTagStrings: string[];
  autoGeneratedTags: string[];
  entryDateTitle: string;
  setTags: (tags: string[]) => void;
  selectedText: string;
  imported?: boolean;
  sticky?: boolean;
  journalAssistant: boolean;
  mentor: string;
  setMentor: React.Dispatch<React.SetStateAction<string>>;
  suggestedMentors: string[];
  setSuggestedMentors: React.Dispatch<React.SetStateAction<string[]>>;
  entryIndex: number;
}) => {
  const {
    entryBackgroundColor,
    blur,
    day,
    date,
    time,
    imported,
    sticky,
    entryTagStrings,
    autoGeneratedTags,
    entryDateTitle,
    setTags,
    selectedText,
    journalAssistant,
    mentor,
    setMentor,
    suggestedMentors,
    setSuggestedMentors,
    entryIndex
  } = params;

  return (
    <div
      className={`tw-flex-none ${sticky ? 'tw-sticky tw-top-0' : ''}`}
      style={{ backgroundColor: entryBackgroundColor, transition: 'background-color 600ms' }}
    >
      <div className="tw-w-full tw-mt-24 tw-flex tw-flex-row tw-justify-start tw-items-center tw-gap-2 tw-pb-6">
        <div
          className={`${blur ? 'tw-blur-sm' : ''} tw-pl-0.5 tw-font-bold tw-text-lg ${
            entryBackgroundColor === Colors.Dark ? 'tw-text-primary-100' : 'tw-text-primary-900'
          }`}
        >
          {`${day}, ${date} - ${time}`}
        </div>
        {imported && (
          <Badge
            className={blur ? 'tw-blur-sm' : ''}
            style={{ fontSize: '8px' }}
            bg={entryBackgroundColor === Colors.White ? 'dark' : 'light'}
            text={entryBackgroundColor === Colors.White ? 'light' : 'dark'}
          >
            imported
          </Badge>
        )}
        {journalAssistant && entryIndex === 0 && (
          <MentorSelector
            mentor={mentor}
            setMentor={setMentor}
            setSuggestedMentors={setSuggestedMentors}
            suggestedMentors={suggestedMentors}
          />
        )}
        {selectedText && (
          <OverlayTrigger placement="right" overlay={tweetItTooltip}>
            <a
              target="_blank"
              rel="noreferrer"
              href={`https://twitter.com/intent/tweet?text=${encodeURI(selectedText.slice(0, 215))}${
                selectedText.length > 215 ? '...' : ''
              }%0A%0Awritten%20with&url=https://jumblejournal.org?lang=en&hashtags=journalEveryDay,journaling`}
              className="tw-relative tw-h-5 tw-w-[73px] tw-box-border tw-py-1 tw-px-3 tw-bg-[#1d9bf0] tw-text-white tw-rounded-full tw-font-medium tw-cursor-pointer tw-no-underline tw-flex tw-items-center tw-font-['Helvetica_Neue',Arial,sans-serif] tw-text-xs tw-leading-[18px] hover:tw-bg-[#147dc2] hover:tw-text-white hover:tw-no-underline"
            >
              <div className="tw-flex tw-flex-row tw-items-start tw-gap-1">
                <Twitter size={14} />
                Tweet
              </div>
            </a>
          </OverlayTrigger>
        )}
      </div>
      <TagList
        entryTagStrings={entryTagStrings}
        creationDate={entryDateTitle}
        darkMode={entryBackgroundColor === Colors.Dark}
        blur={blur}
        setTags={setTags}
        autoGeneratedTags={autoGeneratedTags}
      />
    </div>
  );
};

const backButtonTooltip = <Tooltip id="back-button-tooltip">Save and return to home screen</Tooltip>;

const wordCountTooltip = <Tooltip id="word-count-tooltip">500 words is a great target! 💪</Tooltip>;

const tweetItTooltip = <Tooltip id="word-count-tooltip">Share the highlighted text on Twitter</Tooltip>;
export default SingleEntry;
